'use client'
import React from 'react'
import { Inter } from 'next/font/google'
import { Providers } from '@/providers/providers'
import { SignalRProvider } from '@/contexts/signalr-context'
import { CopilotProvider } from '@/providers/copilot-provider'
import { CopilotChat } from '@/components/ui/copilot'
import { AIBotProvider } from '@/contexts/ai-bot-context'

import { Toaster } from '@/components/ui/sonner-toast'
import { ThemeProvider } from '@/components/theme-provider'
import '../styles/globals.css'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { initBugsnag } from '@/services/bugsnag'
import 'prismjs/themes/prism-tomorrow.css'

const inter = Inter({ subsets: ['latin'] })

// Initialize Bugsnag
const bugsnag = initBugsnag()
const BugsnagErrorBoundary = bugsnag
  .getPlugin('react')!
  .createErrorBoundary(React)

const ErrorFallback = () => (
  <div className="flex min-h-screen flex-col items-center justify-center p-4">
    <h1 className="mb-4 text-2xl font-bold">Something went wrong</h1>
    <p className="text-muted-foreground">
      We&apos;ve been notified and are working to fix the issue.
    </p>
  </div>
)

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
      <html lang="en" suppressHydrationWarning>
        <body className={`${inter.className} h-screen overflow-hidden`}>
          <ThemeProvider
            attribute="class"
            defaultTheme="system"
            enableSystem
            disableTransitionOnChange
            storageKey="spearfish-theme"
          >
            <Providers>
              <SignalRProvider>
                <CopilotProvider>
                  <AIBotProvider>
                    <ErrorBoundary>
                      {children}
                    </ErrorBoundary>
                    <CopilotChat />
                    <Toaster />
                  </AIBotProvider>
                </CopilotProvider>
              </SignalRProvider>
            </Providers>
          </ThemeProvider>
        </body>
      </html>
    </BugsnagErrorBoundary>
  )
}
